import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Line } from "@react-three/drei";
import { observer } from "mobx-react-lite";
import * as THREE from "three";
import {
  SmartTableConfig,
  SmartTableType,
  zIndexEpsilon,
} from "../../types/floorplannerTypes";
import {
  ResolvedSmartTableLayout,
  SmartTableChairPlacement,
  resolveSmartTableLayout,
  resolveSmartTableArmchairProfile,
} from "../../features/floorplan3d/symbols/dining/smartTableLayout";
import { normalizeSmartTableConfig } from "../../features/floorplan3d/symbols/dining/smartTableConfig";
import { CANVAS_ACCENT_COLOR } from "../../constants/brandColors";
import { editorStore } from "../../store/editorStore";
import { FloorplannerStoreContext } from "../../store/floorplannerStore";
import DraggableObject from "./DraggableObject";
import { BoundingBox, SelectableSymbol } from "./SelectableSymbol";
import { resolveFillOpacity } from "../../utils/fillOpacity";
import { getOpacityRemountEpoch } from "../../utils/opacityRemount";

type SmartTableSymbolProps = {
  table: SmartTableType;
  onDragStart: (table: SmartTableType, offset: [number, number]) => void;
  onDrag: (newPosition: [number, number]) => void;
  onDragEnd: (endPosition: [number, number]) => void;
};

const rectanglePoints = (
  width: number,
  height: number,
): Array<[number, number, number]> => [
  [-width / 2, -height / 2, 0.003],
  [width / 2, -height / 2, 0.003],
  [width / 2, height / 2, 0.003],
  [-width / 2, height / 2, 0.003],
  [-width / 2, -height / 2, 0.003],
];

const PlanRect: React.FC<{
  width: number;
  height: number;
  x?: number;
  y?: number;
  fill: THREE.ColorRepresentation;
  stroke: THREE.ColorRepresentation;
  lineWidth: number;
  opacity?: number;
}> = ({ width, height, x = 0, y = 0, fill, stroke, lineWidth, opacity = 1 }) => (
  <group position={[x, y, 0]}>
    <mesh position={[0, 0, 0.001]}>
      <planeGeometry args={[Math.max(width, 0.001), Math.max(height, 0.001)]} />
      <meshBasicMaterial
        color={fill}
        side={THREE.DoubleSide}
        opacity={opacity}
        transparent={opacity < 1}
        depthWrite={opacity >= 1}
      />
    </mesh>
    <Line
      key={`plan-rect-stroke-${Math.round(opacity * 100)}`}
      points={rectanglePoints(width, height)}
      color={stroke}
      lineWidth={lineWidth}
      opacity={opacity}
      transparent={opacity < 1}
      depthWrite={opacity >= 1}
      renderOrder={2}
    />
  </group>
);

const ChairPlan: React.FC<{
  chair: SmartTableChairPlacement;
  stroke: THREE.ColorRepresentation;
  lineWidth: number;
  opacity: number;
}> = ({ chair, stroke, lineWidth, opacity }) => {
  if (chair.model === "armchair") {
    const profile = resolveSmartTableArmchairProfile(
      chair.width,
      chair.depth,
    );
    return (
      <group
        position={[chair.x, chair.y, 0.004]}
        rotation={[0, 0, chair.outwardAngle]}
      >
        <PlanRect
          width={profile.seat.depth}
          height={profile.seat.width}
          x={profile.seat.centerY}
          fill="#ffffff"
          stroke={stroke}
          lineWidth={lineWidth}
          opacity={opacity}
        />
        <PlanRect
          width={profile.arm.depth}
          height={profile.arm.width}
          x={profile.arm.centerY}
          y={profile.arm.centerX}
          fill="#ffffff"
          stroke={stroke}
          lineWidth={lineWidth}
          opacity={opacity}
        />
        <PlanRect
          width={profile.arm.depth}
          height={profile.arm.width}
          x={profile.arm.centerY}
          y={-profile.arm.centerX}
          fill="#ffffff"
          stroke={stroke}
          lineWidth={lineWidth}
          opacity={opacity}
        />
        <PlanRect
          width={profile.back.depth}
          height={profile.back.width}
          x={profile.back.centerY}
          fill="#ffffff"
          stroke={stroke}
          lineWidth={lineWidth}
          opacity={opacity}
        />
      </group>
    );
  }

  const backDepth = chair.depth * (7.08 / 47.22);
  return (
    <group
      position={[chair.x, chair.y, 0.004]}
      rotation={[0, 0, chair.outwardAngle]}
    >
      <PlanRect
        width={chair.depth}
        height={chair.width}
        fill="#ffffff"
        stroke={stroke}
        lineWidth={lineWidth}
        opacity={opacity}
      />
      <PlanRect
        width={backDepth}
        height={chair.width}
        x={chair.depth / 2 - backDepth / 2}
        fill="#ffffff"
        stroke={stroke}
        lineWidth={lineWidth}
        opacity={opacity}
      />
    </group>
  );
};

const SmartTablePlan: React.FC<{
  layout: ResolvedSmartTableLayout;
  fill: THREE.ColorRepresentation;
  stroke: THREE.ColorRepresentation;
  lineWidth: number;
  fillOpacity: number;
}> = ({ layout, fill, stroke, lineWidth, fillOpacity }) => {
  const circlePoints = useMemo(() => {
    const radius = layout.tabletop.width / 2;
    return Array.from({ length: 65 }, (_, index) => {
      const angle = (index / 64) * Math.PI * 2;
      return [
        Math.cos(angle) * radius,
        Math.sin(angle) * radius,
        0.003,
      ] as [number, number, number];
    });
  }, [layout.tabletop.width]);

  return (
    <>
      {layout.tabletop.shape === "round" ? (
        <group>
          <mesh position={[0, 0, 0.001]}>
            <circleGeometry args={[layout.tabletop.width / 2, 64]} />
            <meshBasicMaterial
              color={fill}
              side={THREE.DoubleSide}
              opacity={fillOpacity}
              transparent={fillOpacity < 1}
              depthWrite={fillOpacity >= 1}
            />
          </mesh>
          <Line
            key={`table-circle-stroke-${Math.round(fillOpacity * 100)}`}
            points={circlePoints}
            color={stroke}
            lineWidth={lineWidth}
            opacity={fillOpacity}
            transparent={fillOpacity < 1}
            depthWrite={fillOpacity >= 1}
            renderOrder={2}
          />
        </group>
      ) : (
        <PlanRect
          width={layout.tabletop.width}
          height={layout.tabletop.length}
          fill={fill}
          stroke={stroke}
          lineWidth={lineWidth}
          opacity={fillOpacity}
        />
      )}
      {layout.chairs.map((chair) => (
        <ChairPlan
          key={chair.id}
          chair={chair}
          stroke={stroke}
          lineWidth={lineWidth}
          opacity={fillOpacity}
        />
      ))}
    </>
  );
};

const SmartTableSymbol: React.FC<SmartTableSymbolProps> = observer(
  ({ table, onDragStart, onDrag, onDragEnd }) => {
    const floorplannerStore = React.useContext(FloorplannerStoreContext);
    const groupRef = useRef<THREE.Group>(null);
    const [localConfig, setLocalConfig] = useState<SmartTableConfig>(() =>
      normalizeSmartTableConfig(table.smartTable),
    );
    const configRef = useRef(localConfig);
    const [localPosition, setLocalPosition] = useState<[number, number]>([
      table.position.x,
      table.position.y,
    ]);
    const [isResizing, setIsResizing] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const layout = useMemo(
      () => resolveSmartTableLayout(localConfig),
      [localConfig],
    );
    const lineWidth = table.lineWeight ?? floorplannerStore.symbolLineWeight;
    const lineColor = table.lineColor ?? floorplannerStore.lineColor;
    const activeLineColor =
      isHovered || table.selected ? CANVAS_ACCENT_COLOR : lineColor;
    const fillColor = table.fillColor ?? floorplannerStore.fillColor ?? 0xffffff;
    const fillOpacity = resolveFillOpacity(table);

    const calculateBoundingBox = useCallback((): BoundingBox => {
      const width = layout.tabletop.width;
      const height = layout.tabletop.length;
      return {
        topLeft: [-width / 2, height / 2],
        topRight: [width / 2, height / 2],
        bottomLeft: [-width / 2, -height / 2],
        bottomRight: [width / 2, -height / 2],
        width,
        height,
        depth: 0,
      };
    }, [layout.tabletop.length, layout.tabletop.width]);

    useEffect(() => {
      editorStore.updateGroupRef(table.id, groupRef.current);
    }, [table.id]);

    useEffect(() => {
      if (isResizing) return;
      const next = normalizeSmartTableConfig(table.smartTable);
      configRef.current = next;
      setLocalConfig(next);
      setLocalPosition([table.position.x, table.position.y]);
    }, [
      isResizing,
      table.position.x,
      table.position.y,
      table.smartTable,
    ]);

    return (
      <group position={[0, 0, table.zIndex * zIndexEpsilon]}>
        <DraggableObject
          position={localPosition}
          onDragStart={(offset) => {
            setIsDragging(true);
            onDragStart(table, offset);
          }}
          onDrag={onDrag}
          onDragEnd={(endPosition) => {
            onDragEnd(endPosition);
            setIsDragging(false);
          }}
          selectable
          attachmentId={table.id}
          attachmentType="noAttachments"
          symbol={table}
        >
          <SelectableSymbol
            symbol={table}
            handleSize={floorplannerStore.symbolHandleSize}
            calculateBoundingBox={calculateBoundingBox}
            onResizeStart={() => setIsResizing(true)}
            onResize={(newWidth, newHeight, _bounds, _handle, newPosition) => {
              const next = normalizeSmartTableConfig(
                localConfig.shape === "round"
                  ? {
                      ...localConfig,
                      diameter: Math.max(newWidth, newHeight),
                    }
                  : {
                      ...localConfig,
                      width: newWidth,
                      length: newHeight,
                    },
              );
              configRef.current = next;
              setLocalConfig(next);
              if (newPosition) setLocalPosition(newPosition);
            }}
            onResizeEnd={(_event, finalResize) => {
              setIsResizing(false);
              floorplannerStore.updateSymbolProperty(
                table.id,
                "smartTable",
                configRef.current,
              );
              if (finalResize?.position) {
                floorplannerStore.updateSymbolProperty(
                  table.id,
                  "position",
                  finalResize.position,
                );
              }
            }}
            center={[0, 0]}
            rotation={table.rotation}
            drawHandles
            showMiddleHandles={localConfig.shape === "rectangle"}
            lockAspectRatio={localConfig.shape === "round"}
            isDragging={isDragging}
            onPointerOver={() => setIsHovered(true)}
            onPointerOut={() => setIsHovered(false)}
          >
            <group
              ref={groupRef}
              rotation={[0, 0, table.rotation ?? 0]}
              onPointerEnter={() => {
                if (
                  document.body.style.cursor !== "grabbing" &&
                  document.body.style.cursor !== "move"
                ) {
                  document.body.classList.add("cursor-vector");
                }
              }}
              onPointerLeave={() =>
                document.body.classList.remove("cursor-vector")
              }
            >
              <SmartTablePlan
                key={`smart-table-opacity-${Math.round(fillOpacity * 100)}-${getOpacityRemountEpoch(table.id)}`}
                layout={layout}
                fill={fillColor}
                fillOpacity={fillOpacity}
                stroke={activeLineColor}
                lineWidth={lineWidth}
              />
            </group>
          </SelectableSymbol>
        </DraggableObject>
      </group>
    );
  },
);

export default SmartTableSymbol;
